// client/src/lib/firestore/idInChunks.ts
//
// How many document ids one `where(documentId(), 'in', ids)` read may name, per collection.
//
// WHY THERE IS A CEILING BELOW FIRESTORE'S OWN 30. For a query on NAMED documents the rules engine judges each named
// document on its own (instead of judging the query's filters), and every judgement spends document-access calls
// (`get()` / `exists()` inside helpers such as employeeOrgId() and belongsToOrg()) against a per-request budget. A
// batch past the budget is refused with PERMISSION_DENIED for the WHOLE batch, for a non-admin, while a rules-bypassing
// Admin SDK read of the same ids succeeds (which is how #6910 shipped with every Confirm disabled). A collection whose
// rule spends more calls per document has a lower ceiling (`leads`, which checks org membership per document, fails
// from 19; `serviceTickets` from 20). The same mechanism is documented for #4023 in collections-service.ts.
//
// MEASURED 2026-10-10 as the same non-admin TEST user through the Firestore REST API (status codes only; tools
// `drops/d72-looks/tools/linkreview-chunk-proof.cjs`, `inshape-neighbours-probe*.cjs`): ids per query 5/15/19/20/30.
// SINGLE-ROLE CAVEAT: every number was measured as ONE user (admin + Finance). The budget is spent on rule helpers, so a
// plain employee or manager could land lower. Entries with no measured denial therefore keep the default `use` of 15
// even where 30 was allowed, until a second role is measured.
// `use` is what the app sends: it must stay <= `okAt` and < `deniedAt` (pinned by idInChunks.test.ts).
// A collection NOT in this table gets ID_IN_DEFAULT_MAX: conservative, because it was never measured.
// RE-MEASURE when a collection's `list`/`get` rule or the helpers it calls change.
import { COLLECTIONS } from '../../config/collections';

/** For a collection nobody has measured. Below every measured denial (lowest observed: 19). */
export const ID_IN_DEFAULT_MAX = 15;

export interface IdInCeiling {
  /** Ids per query the app sends. */
  use: number;
  /** Largest count measured ALLOWED (omit when it was not measured). */
  okAt?: number;
  /** Smallest count measured DENIED (omit when none was denied up to 30). */
  deniedAt?: number;
}

export const ID_IN_CEILINGS: Readonly<Record<string, IdInCeiling>> = {
  [COLLECTIONS.SERVICE_TICKETS]: { use: 15, okAt: 19, deniedAt: 20 },
  [COLLECTIONS.LEADS]: { use: 15, okAt: 15, deniedAt: 19 },
  // Reserved: collections-service reads these by an org-scoped list instead (#4023); listed so a future id-list caller is capped.
  [COLLECTIONS.CUSTOMER_COLLECTION_PROFILES]: { use: 15, okAt: 19, deniedAt: 20 },
  // Measured ok to 30 as one admin+Finance user, no denial seen; held at 15 until a plain employee is measured.
  [COLLECTIONS.USERS]: { use: 15, okAt: 30 },
  [COLLECTIONS.APPSHEET_GENERATORS]: { use: 15, okAt: 30 },
  [COLLECTIONS.COLLECTION_ITEMS]: { use: 15, okAt: 30 },
};

/** Ids per query for this collection. */
export function idInMax(collectionName: string): number {
  return ID_IN_CEILINGS[collectionName]?.use ?? ID_IN_DEFAULT_MAX;
}

/**
 * Split ids into reads that stay under the collection's measured ceiling. Duplicates and blank ids are dropped (a blank
 * id is an invalid document reference and would fail the whole batch).
 */
export function idInChunks(collectionName: string, ids: Iterable<string>): string[][] {
  const unique = Array.from(new Set(Array.from(ids).filter((id) => typeof id === 'string' && id.trim() !== '')));
  const size = idInMax(collectionName);
  const out: string[][] = [];
  for (let i = 0; i < unique.length; i += size) out.push(unique.slice(i, i + size));
  return out;
}
